import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FbTableListDashboardCardService } from './fb-table-list-dashboard-card.service';
import { take } from 'rxjs/operators';
import { CustomButtonActionService } from 'custom-button-action';
import { FormDesignerService } from '../../form-designer/form-designer.service';
import { environment } from 'environment-configurations';
import { combineLatest } from 'rxjs';
@Component({
  selector: 'app-fb-table-list-dashboard-card',
  templateUrl: './fb-table-list-dashboard-card.component.html',
  styleUrls: ['./fb-table-list-dashboard-card.component.scss']
})
export class FbTableListDashboardCardComponent implements OnChanges {
  @Input() content: {
    type: string,
    title: string,
    subTitle?: string,
    footerLink: string,
    apiValue: string,
    dashboardRedirectPageFormId?: number,
    headerRedirectButton?: any[],
    tableClass?: string,
    headerFilters?: any[],
    showMore?: any,
    tableScrollableHeight?: number,
    minTableHeight?: number,
    apiType?: string,
    globalsearch?: boolean,
    moveControlsToNextLine?: boolean
  };

  columns: any[] = [];
  tableList: any[] = [];

  private config = {
    'Employee Leave Status': {
      columns: [
        { field: 'leaveDate', header: 'Date' },
        { field: 'leaveType', header: 'Leave Type' },
        { field: 'status', header: 'Status' }
      ],
      transform: (data) => this.transformEmployeeLeaveStatus(data)
    },
    'Leave Balance': {
      columns: [
        { field: 'leaveType', header: 'Leave Type' },
        { field: 'leaveTypeCode', header: 'Leave Code' },
        { field: 'leaveBalance', header: 'Balance' }
      ],
      transform: (data) => data.map(item => ({
        leaveType: item.leaveType,
        leaveTypeCode: item.leaveTypeCode,
        leaveBalance: item.leaveBalance ? item.leaveBalance.toFixed(2) : 0
      }))
    },
    'Timesheet Summary': {
      columns: [
        { field: 'name', header: 'Name', class: 'w-180px', tdClass: 'w-180px' },
        { field: 'firstHalf', header: 'First Half' },
        { field: 'secondHalf', header: 'Second Half' }
      ],
      transform: (data) => data.map(item => ({
        name: item.name,
        firstHalf: item.firstHalf,
        secondHalf: item.secondHalf
      }))
    },
    'Job Openings': {
      columns: [
        { field: 'jobId', header: 'Job ID', class: 'w-60px', tdClass: 'td-data-blue w-60px' },
        {
          field: 'status', header: 'Status', class: 'w-80px', tdClass: 'w-80px', conditionalTdClass: [{
            name: 'td-data-blue',
            fieldEqualsTo: 'In Progress'
          }, {
            name: 'td-data-green',
            fieldEqualsTo: 'Open'
          }]
        },
        { field: 'position', header: 'Position', tdClass: 'text-wrap', showInfoText: true, infoTextProperty: 'positionInfoText' },
        { field: 'client', header: 'Client' },
        { field: 'location', header: 'Location' },
        { field: 'submissions', header: 'Submissions', class: 'w-100px', tdClass: 'w-100px' },
        { field: 'accountManager', header: 'Requestor' },
        { field: 'recruiters', header: 'Assignee', tdClass: 'text-wrap' }
      ],
      transform: (data) => data.map(item => ({
        jobId: item.jobId,
        status: item.status,
        position: item.position,
        client: item.client,
        location: item.location,
        submissions: item.submissions,
        accountManager: item.accountManager,
        recruiters: Array.isArray(item.recruitersName) && item.recruitersName.length > 0 ? item.recruitersName.join(', ') : '',
        positionInfoText: item.positionInfoText
      }))
    },
    'Activity Report': {
      columns: [
        { field: 'role', header: 'Position', class: 'w-180px', tdClass: 'text-wrap w-180px' },
        { field: 'noOfPositions', header: 'Open Positions', class: 'header-column-wrap' },
        { field: 'avgDaysToFill', header: 'Average Days to Fill', class: 'header-column-wrap' },
        { field: 'submittedCandidates', header: '# Submitted Applicants', class: 'header-column-wrap' },
        { field: 'selectedCandidates', header: '# Selected Applicants', class: 'header-column-wrap' },
        { field: 'offerReleased', header: 'Offer Released', class: 'header-column-wrap' },
        { field: 'onboardCount', header: 'Onboard' },
        { field: 'backouts', header: 'Backouts' }
      ],
      transform: (data) => data.map(item => ({
        role: item.role,
        noOfPositions: item.noOfPositions,
        avgDaysToFill: item.avgDaysToFill,
        submittedCandidates: item.submittedCandidates,
        selectedCandidates: item.selectedCandidates,
        offerReleased: item.offerReleased,
        onboardCount: item.onboardCount,
        backouts: item.backouts
      }))
    },
    'Turn Around Time (TAT)': {
      columns: [
        { field: 'jobPosition', header: 'Position' },
        { field: 'client', header: 'Client' },
        { field: 'turnAroundTime', header: 'TAT (days)' }
      ],
      transform: (data) => data.map(item => ({
        jobPosition: item.jobPosition,
        client: item.client,
        turnAroundTime: item.turnAroundTime
      }))
    },
    'Turnaround Time and Fill Ratio': {
      columns: [
        { field: 'jobPosition', header: 'Position' },
        { field: 'turnAroundTime', header: 'Turnaround Time' },
        { field: 'timeToFillRatio', header: 'Time to Fill' }
      ],
      transform: (data) => data.map(item => ({
        jobPosition: item.jobPosition,
        turnAroundTime: this.formatTurnAroundTime(item.turnAroundTime),
        timeToFillRatio: this.formatTurnAroundTime(item.timeToFillRatio)
      }))
    },
    'Resume Source': {
      columns: [
        { field: 'source', header: 'Source' },
        { field: 'candidates', header: '# of candidates' },
        { field: 'percentageOfHired', header: '% of Hired' },
        { field: 'conversionRate', header: 'Conv Rate' }
      ],
      transform: (data) => data.map(item => ({
        source: item.source,
        candidates: item.candidates,
        percentageOfHired: item.percentageOfHired ? item.percentageOfHired + '%' : '0%',
        conversionRate: item.conversionRate ? item.conversionRate + '%' : ''
      }))
    },
    'Top 5 Recent Submissions': {
      columns: [
        { field: 'candidateName', header: 'Applicant Name', class: 'w-110px', tdClass: 'text-wrap w-110px' },
        { field: 'position', header: 'Position', class: 'w-100px', tdClass: 'text-wrap w-100px' },
        { field: 'resume', header: 'Resume', tdClass: 'text-wrap' },
        { field: 'appliedOn', header: 'Applied On', class: 'header-column-wrap', tdClass: 'text-wrap' },
      ],
      transform: (data) => data.map(item => ({
        candidateName: item.candidateName,
        position: item.position,
        resume: item.resume,
        appliedOn: item.appliedOn
      }))
    },
    'Quick Interview Scheduling': {
      columns: [
        { field: 'candidateName', header: 'Candidate Name', class: 'text-wrap', tdClass: 'text-wrap' },
        { field: 'role', header: 'Job Position', tdClass: 'text-wrap' },
        { field: 'client', header: 'Client', tdClass: 'text-wrap' },
        { field: 'resume', header: 'Resume', tdClass: 'text-wrap' },
        { type: 'action', header: 'Action', actionType: 'button', buttonName: 'Schedule', isRedirect: true, redirectPageFormID: 235 }
      ],
      transform: (data) => data.map(item => ({
        candidateName: item.candidateName,
        role: item.role,
        client: item.client,
        resume: item.resume,
        rowData: item
      }))
    },
    'My Candidates': {
      columns: [
        { field: 'id', header: 'ID', class: 'w-60px', tdClass: 'text-wrap w-60px' },
        { field: 'applicantName', header: 'Applicant Name', class: 'text-wrap w-65px', tdClass: 'text-wrap w-65px' },
        { field: 'email', header: 'Email', tdClass: 'text-wrap' },
        { field: 'jobTitle', header: 'Job Title', class: 'text-wrap', tdClass: 'text-wrap' },
        { field: 'source', header: 'Source', tdClass: 'text-wrap' },
        { field: 'yearsOfExperience', header: 'Years of Experience', class: 'text-wrap w-75px', tdClass: 'text-wrap w-75px' }
      ],
      transform: (data) => data.map(item => ({
        id: item.id,
        applicantName: item.applicantName,
        email: item.email,
        jobTitle: item.jobTitle,
        source: item.source,
        yearsOfExperience: item.yearsOfExperience
      }))
    },
    'Onboarding Status': {
      columns: [
        { field: 'applicantName', header: 'Applicant Name', class: 'text-wrap', tdClass: 'text-wrap' },
        { field: 'clientName', header: 'Client Name', tdClass: 'text-wrap' },
        { field: 'source', header: 'Source', tdClass: 'text-wrap' },
        { field: 'status', header: 'Status', class: 'text-wrap', tdClass: 'text-wrap' }
      ],
      transform: (data) => data.map(item => ({
        id: item.id,
        applicantName: item.applicantName,
        clientName: item.clientName,
        source: item.source,
        status: item.status
      }))
    },
    'Interview Status': {
      columns: [
        { field: 'position', header: 'Position', class: 'text-wrap', tdClass: 'text-wrap' },
        { field: 'noOfApplicants', header: '# of Interviews', tdClass: 'text-wrap' },
        { field: 'outcomePercentage', header: 'Outcome %', tdClass: 'text-wrap' }
      ],
      transform: (data) => data.map(item => ({
        position: item.position,
        noOfApplicants: item.numberOfInterviews,
        outcomePercentage: item.outcome
      }))
    },
    'Onboarding / Dropouts': {
      columns: [
        { field: 'position', header: 'Position' },
        { field: 'onboarding', header: 'Onboarding' },
        { field: 'dropouts', header: 'Dropouts' }
      ],
      transform: (data) => data.map(item => ({
        position: item.position,
        onboarding: item.onboarding,
        dropouts: item.dropouts
      }))
    }
  };
  buttonArray: any[];
  filterArray: any[];
  months: { key: string, value: string }[] = [
    { key: 'All', value: 'ALL' },
    { key: 'January', value: 'JANUARY' },
    { key: 'February', value: 'FEBRUARY' },
    { key: 'March', value: 'MARCH' },
    { key: 'April', value: 'APRIL' },
    { key: 'May', value: 'MAY' },
    { key: 'June', value: 'JUNE' },
    { key: 'July', value: 'JULY' },
    { key: 'August', value: 'AUGUST' },
    { key: 'September', value: 'SEPTEMBER' },
    { key: 'October', value: 'OCTOBER' },
    { key: 'November', value: 'NOVEMBER' },
    { key: 'December', value: 'DECEMBER' }
  ];
  outcome: { key: string, value: string }[] = [
    { key: 'Selected', value: 'Selected' },
    { key: 'Rejected', value: 'Rejected' }
  ];
  selectedMonth: string;
  years: { key: number, value: number }[] = [];
  selectedYear: number;
  configItem: any;
  showMore: boolean;
  isShowMore = false;
  onShowMoreCompleteTableList: any[] = [];
  numberOfRowsLimitOnShowMore: number;
  @Input() contentInUse: {
    type: string,
    title: string,
    subTitle?: string,
    footerLink: string,
    apiValue: string,
    dashboardRedirectPageFormId?: number,
    headerRedirectButton?: any[],
    tableClass?: string,
    headerFilters?: any[],
    showMore?: any,
    tableScrollableHeight?: number,
    minTableHeight?: number,
    apiType?: string,
    globalsearch?: boolean,
    moveControlsToNextLine?: boolean
  };
  isTableScrollable: boolean;
  tableScrollableHeight: number = 0;
  tooltipSelectedMonth: string;
  selectedOutcome: string;
  searchQuery: string = '';

  constructor(
    private fbTableListDashboardCardService: FbTableListDashboardCardService,
    private customButtonActionService: CustomButtonActionService,
    private formDesignerService: FormDesignerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.content && this.content) {
      this.contentInUse = JSON.parse(JSON.stringify(this.content));
      this.fetchDetails();
    }
  }
  stripHtmlTags(str: string): string {
    return str.replace(/(&nbsp;|<\/?[^>]+(>|$))/g, ' ');
  }

  getConditionalClass(rowData: any, col: any): string {
    if (col.conditionalTdClass) {
      for (let condition of col.conditionalTdClass) {
        if (rowData[col.field] === condition.fieldEqualsTo) {
          return condition.name;
        }
      }
    }
    return '';
  }

  private fetchDetails(): void {
    this.configItem = this.config[this.contentInUse.title];
    if (this.configItem) {
      this.columns = this.configItem.columns;
      if (this.contentInUse.apiValue) {
        this.showMore = !!this.contentInUse.showMore;
        if (this.showMore) {
          this.numberOfRowsLimitOnShowMore = this.contentInUse.showMore.itemsShownOnLoad;
        }
        this.isTableScrollable = !!this.content.tableScrollableHeight;
        if (this.isTableScrollable) {
          this.tableScrollableHeight = this.content.tableScrollableHeight;
        }
        this.buttonArray = Array.isArray(this.contentInUse.headerRedirectButton) ? this.contentInUse.headerRedirectButton : null;
        this.filterArray = Array.isArray(this.contentInUse.headerFilters) ? this.contentInUse.headerFilters.map((x: any, index: number) => {
          return {
            ...x,
            filterIndex: index
          }
        }) : null;
        if (Array.isArray(this.filterArray) && this.filterArray.length) {
          if (this.contentInUse.apiType !== 'post') {
            this.contentInUse.apiValue = this.generateUrl(this.contentInUse.apiValue, JSON.parse(JSON.stringify(this.filterArray)));
          }
          if (this.filterArray.some(x => x.filterType === 'Year')) {
            const yearFilter = this.filterArray.find(x => x.filterType === 'Year');
            this.populateYears(yearFilter);
            this.selectedYear = new Date().getFullYear();
          }
          if (this.filterArray.some(x => x.filterType === 'Month')) {
            const currentMonthIndex = new Date().getMonth();
            this.selectedMonth = this.months[currentMonthIndex + 1].value;
            // this.updateTooltipMonth(this.selectedMonth);
          }
          if (this.filterArray.some(x => x.filterType === 'Outcome')) {
            this.selectedOutcome = this.outcome[0].value;
          }
          if (this.filterArray.some(x => ['referenceLibrary', 'APIValues'].includes(x.filterType))) {
            const refAPIReq = this.filterArray.filter(x => x.filterType === 'referenceLibrary' && x.referenceLibraryCategoryName).map(x => {
              return this.fbTableListDashboardCardService.getGroupReferenceData(x.referenceLibraryCategoryName, x.filterIndex);
            });
            const normalAPIReq = this.filterArray.filter(x => x.filterType === 'APIValues' && x.api).map(x => {
              return this.fbTableListDashboardCardService.getAPI(x.api, x.filterIndex);
            });
            const allAPIRequests = [...refAPIReq, ...normalAPIReq];
            if (Array.isArray(allAPIRequests) && allAPIRequests.length) {
              combineLatest(allAPIRequests)
                .pipe(take(1))
                .subscribe((data: any[]) => {
                  data.forEach((result: any) => {
                    const filter = this.filterArray[result.index];
                    if (Array.isArray(result.data) && result.data.length) {
                      let dropDownArray = result.data.map(x => {
                        if (filter.filterType === 'referenceLibrary') {
                          return {
                            value: x.name,
                            name: x.desc
                          };
                        } else if (filter.filterType === 'APIValues') {
                          if (typeof x === 'string') {
                            return {
                              value: x,
                              name: x
                            };
                          }
                          return {
                            value: x.id,
                            name: x.description
                          };
                        }
                      });
                      filter['dropDownArray'] = dropDownArray;
                      if (filter.multiSelect) {
                        filter.selectedReferenceValue = dropDownArray.map(x => x.value);
                      } else {
                        filter.selectedReferenceValue = dropDownArray[0].value;
                      }
                    }
                  });
                  this.getDetailsPostFilter();
                });
            } else {
              this.getDetailsPostFilter();
            }
          } else {
            this.getDetailsPostFilter();
          }
        } else {
          this.apiCall(this.contentInUse.apiValue);
        }
      } else {
        this.tableList = this.configItem.transform([]);
      }
    }
  }

  updateTooltipMonth(selectedMonth) {
    this.tooltipSelectedMonth = this.months.find(x => x.value === selectedMonth).key;
  }

  updateTooltipReferenceLibrary(filterObj: any) {
    filterObj.tooltipSelectedValue = filterObj.dropDownArray.find(x => x.value === filterObj.selectedReferenceValue).name;
  }

  // private getSelectedItemsLabel(index: number) {
  //     this.filterArray[index].selectedItemsLabel = this.filterArray[index].selectedReferenceValue.length === this.filterArray[index].dropDownArray.length ? 'All Positions' : `${this.filterArray[index].selectedReferenceValue.length} items selected`;
  // }

  postApiCall(apiValue: string, payload: any) {
    this.fbTableListDashboardCardService.getPostDetails(apiValue, payload)
      .pipe(take(1))
      .subscribe(data => {
        this.onShowMoreCompleteTableList = this.configItem.transform(data);
        if (this.showMore) {
          if (this.onShowMoreCompleteTableList.length > this.numberOfRowsLimitOnShowMore) {
            this.tableList = this.onShowMoreCompleteTableList.slice(0, this.numberOfRowsLimitOnShowMore);
          } else {
            this.tableList = this.onShowMoreCompleteTableList.slice(0);
          }
        } else {
          this.tableList = this.onShowMoreCompleteTableList.slice(0);
        }
      });
  }

  apiCall(apiValue: string) {
    this.fbTableListDashboardCardService.getDetails(apiValue)
      .pipe(take(1))
      .subscribe(data => {
        this.onShowMoreCompleteTableList = this.configItem.transform(data);
        if (this.showMore) {
          if (this.onShowMoreCompleteTableList.length > this.numberOfRowsLimitOnShowMore) {
            this.tableList = this.onShowMoreCompleteTableList.slice(0, this.numberOfRowsLimitOnShowMore);
          } else {
            this.tableList = this.onShowMoreCompleteTableList.slice(0);
          }
        } else {
          this.tableList = this.onShowMoreCompleteTableList.slice(0);
        }
      });
  }

  getDetailsPostFilter() {
    const regexReferenceLibraryPattern = /\$[a-zA-Z]+-reference/g;
    if (!this.configItem || !this.contentInUse.apiValue || (this.contentInUse.apiValue.includes('$month') && !this.selectedMonth) || (this.contentInUse.apiValue.includes('$year') && !this.selectedYear) || (this.contentInUse.apiValue.includes('$outcome') && !this.selectedOutcome)) {
      return;
    }
    if (this.contentInUse.apiValue && this.contentInUse.apiType && this.contentInUse.apiType === 'post') {
      const selectedReferenceValue = this.filterArray.find(x => x.filterType === 'APIValues').selectedReferenceValue;
      this.postApiCall(this.contentInUse.apiValue, selectedReferenceValue);
    } else {
      let apiValue = this.contentInUse.apiValue;
      if (apiValue.includes('$month')) {
        apiValue = apiValue.replace('$month', this.selectedMonth);
      }
      if (apiValue.includes('$year')) {
        apiValue = apiValue.replace('$year', String(this.selectedYear));
      }
      if (apiValue.includes('$outcome')) {
        apiValue = apiValue.replace('$outcome', String(this.selectedOutcome));
      }
      const matches = this.contentInUse.apiValue.match(regexReferenceLibraryPattern);
      if (Array.isArray(matches) && matches.length) {
        matches.forEach((match: any) => {
          const selectedReferenceValue = this.filterArray.find(x => x.apiVarible === match).selectedReferenceValue;
          if (selectedReferenceValue) {
            apiValue = apiValue.replace(match, String(selectedReferenceValue));
          }
        });
      }
      this.apiCall(apiValue);
    }
  }

  populateYears(yearFilter: any): void {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - yearFilter.numberOfPastYears;
    const endYear = currentYear + yearFilter.numberOfFutureYears;

    for (let year = startYear; year <= endYear; year++) {
      this.years.push({ key: year, value: year });
    }
  }

  generateUrl(baseUrl: string, filters: any[]): string {
    let url = baseUrl;
    const sortedFilters = filters.sort((a, b) => a.apiOrder - b.apiOrder);

    sortedFilters.forEach(filter => {
      const paramName = filter.apiFilterParamName;
      const variableName = filter.apiVarible;
      if (paramName && variableName) {
        url += `/${paramName}/${variableName}`;
      }
    });

    return url;
  }

  private transformEmployeeLeaveStatus(data: any[]): any[] {
    return data.map(item => {
      const leaveTypeCode = item.leaveType.split(' ')[0].charAt(0).toUpperCase();
      return {
        leaveDate: this.formatDate(item.leaveDate),
        leaveType: `${item.leaveType} (${leaveTypeCode})`,
        status: item.status
      };
    });
  }

  private formatTurnAroundTime(turnAroundTime: number): string {
    if (turnAroundTime === undefined || turnAroundTime === null || turnAroundTime < 0) {
      return '-';
    }

    if (turnAroundTime === 0) {
      return '0 days';
    }

    return turnAroundTime === 1 ? '1 day' : `${turnAroundTime} days`;
  }

  private formatDate(dateStr: string): string {
    const dateParts = dateStr.split('-');
    return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
  }

  redirect(pageFormId: number) {
    sessionStorage.removeItem('redirectId');
    sessionStorage.setItem('redirectId', pageFormId.toString());
    this.formDesignerService.setDashboardLink(true);
    this.customButtonActionService.getPageFormById(pageFormId)
      .pipe(take(1)).subscribe((response: any) => {
        if (response) {
          const item = {
            formType: response.buildFormatTypeId,
            pageFormId: response.id
          }
          // if (this.isBeneficiaryProfileListing) {
          //   sessionStorage.setItem('FbEmployeeId', rowData.fb_beneficiaryId);
          //   sessionStorage.setItem('FbActualEmployeeId', rowData.fb_employeeId);
          // } else {
          // sessionStorage.setItem('formDataId', rowData.id);
          // }
          this.customButtonActionService.onLinkRedirectPageSubject.next(item);
        }
      });
  }

  onShowMore() {
    this.isShowMore = !this.isShowMore;
    if (this.isShowMore) {
      this.tableList = this.onShowMoreCompleteTableList.slice(0);
    } else {
      this.tableList = this.onShowMoreCompleteTableList.slice(0, this.numberOfRowsLimitOnShowMore);
    }
  }

  openDoc(item) {
    const baseUrl = environment.docs;
    const docPath = item.substring(23);
    window.open(`${baseUrl}${docPath}`, '_blank');
  }

  onButtonActionClick(col: any, rowData: any) {
    if (col.isRedirect && col.redirectPageFormID && col.buttonName === 'Schedule') {
      if (rowData?.rowData) {
        this.customButtonActionService.getPageFormById(col.redirectPageFormID)
          .pipe(take(1)).subscribe((response: any) => {
            if (response) {
              const item = {
                formType: response.buildFormatTypeId,
                pageFormId: response.id
              }
              this.setOrRemoveSessionStorageItem('fbFormDataDTO', JSON.stringify([rowData.rowData]));
              this.setOrRemoveSessionStorageItem('interviewProcessSetupId', rowData.rowData.interviewProcessSetupId)
              this.setOrRemoveSessionStorageItem('AttendanceDataId', rowData.rowData.id);
              this.setOrRemoveSessionStorageItem('idToGetDetails', rowData.rowData.id);
              this.setOrRemoveSessionStorageItem('applicantId', rowData.rowData.applicantId);
              this.setOrRemoveSessionStorageItem('applicantInfoId', rowData.rowData.applicantClientJobPositionsId);
              this.customButtonActionService.onLinkRedirectPageSubject.next(item);
            }
          });
      }
    }
  }

  private setOrRemoveSessionStorageItem(key: string, value: any) {
    if (value !== null && value !== undefined) {
      sessionStorage.setItem(key, value);
    } else if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
    }
  }

  onSearchChange() {
    this.tableList = this.onShowMoreCompleteTableList.filter(row => {
      return Object.values(row).some(val => {
        try {
          const stringValue = String(val);
          return stringValue.toLowerCase().includes(this.searchQuery.toLowerCase());
        } catch (e) {
          return false;
        }
      });
    });
  }

}
